import { useEffect, useState } from 'react'
import Section, { SECTION_VARIANTS } from 'components/Section'
import classNames from 'classnames'
import { useMemoizedContentGetter } from 'utils/contentful'
import { Typography, LinkButton } from 'components/Contentful'
import { Slider } from '@achieve/ascend'
import { IconListItem } from 'components/IconListItem'
import { addUrlParameter } from 'utils/shared'
import { ChevronRight } from 'react-feather'

const SliderLanding = ({ content }) => {
  const memoizedContent = useMemoizedContentGetter(content, ['title', 'cardList', 'sliderConfig'])
  const { title, cardList, sliderConfig } = memoizedContent
  const [sliderValue, setSliderValue] = useState(0)
  const dollarUSLocale = Intl.NumberFormat('en-US')
  const [ctaHref, setCtaHref] = useState('')
  useEffect(() => {
    setSliderValue(
      sliderConfig?.jsonContent?.slider?.defaultValue
        ? sliderConfig?.jsonContent?.slider?.defaultValue
        : sliderConfig?.jsonContent?.slider?.max / 2
    )
  }, [sliderConfig])
  useEffect(() => {
    const queryParameter = sliderConfig?.jsonContent?.queryParameter || 'loan_amount'
    setCtaHref(
      addUrlParameter(`${sliderConfig?.jsonContent?.ctaHref}`, queryParameter, sliderValue)
    )
  }, [sliderConfig, sliderValue])
  function handleChange(e) {
    setSliderValue(e.target.value)
  }

  const variantBg =
    sliderConfig?.jsonContent?.sectionVariant ?? SECTION_VARIANTS.achieveHighlightBlue
  const showCheveronRight = sliderConfig?.jsonContent?.showCheveronRight ?? false

  return (
    <Section
      className={classNames('section relative overflow-hidden p-0 md:px-0 md:py-4', {
        sectionVariant: variantBg,
        sectionStyle: null,
        layoutImage: 'responsive',
      })}
      childContainerClassName={classNames(
        'min-h-[512px] grid px-0 py-6 md:min-h-[544px] md:px-0 md:py-[50px]',
        {
          'py-5 md:py-[50px]': sliderConfig?.jsonContent?.condensedSection,
        }
      )}
      data-testid="slider-landing-section"
      variant={variantBg}
    >
      <div className={'flex justify-center items-center'}>
        <Typography
          content={title?.textContent}
          variant="displayLg"
          mobileVariant="headingMd"
          breakpoint="md"
          responsive={true}
          fontWeight={sliderConfig?.jsonContent?.titleSemiFontWeight ? 'semibold' : 'bold'}
          className={'text-center '}
        />
      </div>
      {cardList?.iconCardItemCollection?.items?.length > 0 && (
        <div className={'flex justify-center items-center'}>
          <ul
            className={classNames(
              'm-0 p-0 list-none',
              '[&_li]:mb-1',
              '[&_li]:[&_div]:flex-nowrap',
              '[&_p]:pl-2',
              '[&_svg]:stroke-green-600'
            )}
          >
            {cardList?.iconCardItemCollection?.items.map((icon, index) => {
              return (
                <IconListItem
                  key={`icon-list-item-${index}`}
                  iconColor={icon?.iconColor}
                  iconName={icon?.iconName}
                  itemText={icon?.description}
                  classNameText={'leading-[0] text-[15px]'}
                  variant="bodySm"
                  iconSize={20}
                />
              )
            })}
          </ul>
        </div>
      )}
      <div className={'flex justify-center items-center'}>
        <div
          className={
            'w-full max-w-[425px] bg-neutral-0 shadow-[0_3.3px_18px_-3px_rgb(16_24_40_/_9%),0_5px_6px_-2px_rgb(16_24_40_/_4%)] m-0 px-8 py-6 rounded-[12px]'
          }
        >
          {sliderConfig?.jsonContent?.statsTitle && (
            <Typography
              content={sliderConfig?.jsonContent?.statsTitle}
              variant="displaySm"
              fontWeight="medium"
              data-testid="slider-title"
              className={'text-center pb-4'}
            />
          )}
          <Typography
            content={`$${dollarUSLocale.format(sliderValue)}${
              sliderValue === sliderConfig?.jsonContent?.slider?.max &&
              sliderConfig?.jsonContent?.slider?.maxPlus
                ? '+'
                : ''
            }`}
            variant="displaySm"
            fontWeight="bold"
            className={'text-blue-600 text-center pb-2'}
            data-testid="slider-dollar-value"
          />
          <Slider
            aria-label="dollar-amount-selection-slider"
            className={classNames(
              'w-full',
              '[class^="MuiSlider-thumb"]:h-6 [class^="MuiSlider-thumb"]:w-6',
              '[class^="MuiSlider-track"]:h-[5px] [class^="MuiSlider-track"]:border-0',
              '[class^="MuiSlider-rail"]:opacity-100 [class^="MuiSlider-rail"]:h-[5px] [class^="MuiSlider-rail"]:border-0'
            )}
            classes={{
              mark: 'hidden',
            }}
            data-testid="slider-input"
            value={sliderValue}
            step={sliderConfig?.jsonContent?.slider?.step}
            min={sliderConfig?.jsonContent?.slider?.min}
            max={sliderConfig?.jsonContent?.slider?.max}
            marks={[
              {
                value: sliderConfig?.jsonContent?.slider?.min,
                label: (
                  <Typography
                    content={`$${dollarUSLocale.format(sliderConfig?.jsonContent?.slider?.min)}`}
                    variant="bodyXs"
                    className={'ml-[36px]'}
                    data-testid="slider-min"
                  />
                ),
              },
              {
                value: sliderConfig?.jsonContent?.slider?.max,
                label: (
                  <Typography
                    content={`$${dollarUSLocale.format(sliderConfig?.jsonContent?.slider?.max)}${
                      sliderConfig?.jsonContent?.slider?.maxPlus ? '+' : ''
                    }`}
                    variant="bodyXs"
                    className={'-ml-[60px]'}
                    data-testid="slider-max"
                  />
                ),
              },
            ]}
            onChange={handleChange}
          />
          <LinkButton
            track={{
              ...{
                list_name: 'Slider landing CTA click',
                click_id: sliderConfig?.jsonContent?.ctaText,
                click_text: sliderConfig?.jsonContent?.ctaText,
                form_ss_amount: sliderValue,
                click_url: ctaHref,
                click_type: 'Button Click',
                event_action: 'button_click',
                nav_link_section: 'Slider Landing',
                track_event: 'internal_campaign_click',
              },
            }}
            content={{
              linkHref: ctaHref,
              linkText: sliderConfig?.jsonContent?.ctaText,
            }}
            typographicOptions={{ variant: 'bodyLg', fontWeight: 'bold' }}
            variant="contained"
            color="primary"
            className={
              'min-w-max mt-4 [&_span]:bg-neutral-0 [&_span]:rounded-lg [&_span]:[&_svg]:text-blue-600'
            }
            fullWidth
            {...(showCheveronRight && { endIcon: <ChevronRight size={14} /> })}
          />
        </div>
      </div>
    </Section>
  )
}

export default SliderLanding
